import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Pin, PinOff, Trash2, StickyNote } from "lucide-react";
import { toast } from "sonner";
import { useConfirm } from "@/components/ConfirmDialog";
import { useAuth } from "@/contexts/AuthContext";

/**
 * NotesPanel — reusable internal-notes list for customer/vehicle/contract.
 * Owner/admin can create/edit/delete any note. Mechanics can only touch
 * their own notes (backend enforces the same rule).
 */
export default function NotesPanel({ entityType, entityId }) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const confirm = useConfirm();
  const [notes, setNotes] = useState([]);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);

  const load = async () => {
    if (!entityId) return;
    try {
      const { data } = await api.get(`/notes`, {
        params: { entity_type: entityType, entity_id: entityId },
      });
      setNotes(data || []);
    } catch (_) {
      setNotes([]);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [entityType, entityId]);

  const add = async () => {
    if (!text.trim()) return;
    setLoading(true);
    try {
      await api.post("/notes", {
        entity_type: entityType, entity_id: entityId, text: text.trim(),
      });
      setText("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally {
      setLoading(false);
    }
  };

  const togglePin = async (n) => {
    try {
      await api.patch(`/notes/${n.id}`, { pinned: !n.pinned });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  const del = async (n) => {
    if (!(await confirm())) return;
    try {
      await api.delete(`/notes/${n.id}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  const canEditNote = (n) =>
    user?.role === "owner" || user?.role === "admin" || n.author_id === user?.id;

  return (
    <div className="space-y-3" data-testid="notes-panel">
      <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-zinc-500">
        <StickyNote className="h-3.5 w-3.5" />
        {t("notes.title")}
      </div>

      <div className="flex gap-2">
        <Textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={t("notes.placeholder")}
          rows={2}
          className="bg-[#171B24] border-[#2E3241] flex-1"
          data-testid="notes-input"
        />
        <Button
          onClick={add}
          disabled={loading || !text.trim()}
          className="bg-blue-500 hover:bg-blue-600 self-end"
          data-testid="notes-add-btn"
        >
          {t("notes.add")}
        </Button>
      </div>

      {notes.length === 0 ? (
        <div className="text-sm text-zinc-500 italic">{t("notes.empty")}</div>
      ) : (
        <ul className="space-y-2">
          {notes.map((n) => (
            <li
              key={n.id}
              className={`rounded-lg border p-3 ${
                n.pinned
                  ? "bg-amber-500/5 border-amber-500/30"
                  : "bg-[#171B24] border-[#2E3241]"
              }`}
              data-testid={`note-${n.id}`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="text-sm text-zinc-100 whitespace-pre-wrap flex-1">
                  {n.text}
                </div>
                {canEditNote(n) && (
                  <div className="flex items-center gap-1 shrink-0">
                    <button
                      onClick={() => togglePin(n)}
                      className="p-1 text-zinc-400 hover:text-amber-400"
                      title={n.pinned ? t("notes.unpin") : t("notes.pin")}
                      data-testid={`note-pin-${n.id}`}
                    >
                      {n.pinned
                        ? <PinOff className="h-3.5 w-3.5" />
                        : <Pin className="h-3.5 w-3.5" />}
                    </button>
                    <button
                      onClick={() => del(n)}
                      className="p-1 text-zinc-400 hover:text-red-400"
                      data-testid={`note-del-${n.id}`}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                )}
              </div>
              <div className="mt-2 text-[11px] text-zinc-500">
                {t("notes.by")} {n.author_name || "—"} ·{" "}
                {n.created_at ? new Date(n.created_at).toLocaleString() : ""}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
